import { Box, Button, Flex, Input, Text, VStack } from "@chakra-ui/react";
import React, { useMemo, useRef, useState } from "react";
import { MdVideocam } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import PdfPreviewModal, {
  liveClassworkPdfUrl,
} from "./PdfPreviewModal";
import { studentPalette } from "../../constants/studentPalette";
import { studentSurfaces } from "../../constants/studentThemeSurfaces";
import { useCountdownNow } from "../../hooks/useStartsInLabel";
import {
  setLiveVideoSeenRequest,
  uploadLiveClassworkRequest,
} from "../../modules/students-modules/videos/Actions";
import {
  canJoinLiveSession,
  getLiveAttendanceId,
  getLiveJoinButtonLabel,
  getLiveJoinUrl,
  getLiveSessionPhase,
} from "../../utils/liveSessionStatus";

const C = {
  primary: studentPalette.primary,
  secondary: studentPalette.secondary,
  cream: studentPalette.cream,
  border: studentSurfaces.border,
  paper: studentSurfaces.raised,
  muted: studentSurfaces.textMuted,
};

const thinScrollCss = {
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(91, 13, 22, 0.35) transparent",
  "&::-webkit-scrollbar": { width: "4px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(91, 13, 22, 0.35)",
    borderRadius: "999px",
  },
  "&::-webkit-scrollbar-button": { display: "none", height: 0, width: 0 },
};

function liveTitle(lv) {
  return lv?.title || lv?.name || "Live session";
}

function isAttended(lv) {
  return lv?.attended === true || lv?.attended === 1 || lv?.attended === "1";
}

function hasClasswork(lv) {
  return lv?.classwork != null && String(lv.classwork).trim() !== "";
}

/** Shared live-session row/card actions used on home + course curriculum. */
export function LiveSessionJoinControls({ lv, size = "sm", fullWidth = false }) {
  const dispatch = useDispatch();
  const seenLoading = useSelector((s) => s.chapterVideos.seenLoading);
  const fileRef = useRef(null);
  const [localUploaded, setLocalUploaded] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const now = useCountdownNow(true, 1000);
  const phase = getLiveSessionPhase(lv?.start_time, now);
  const join = getLiveJoinUrl(lv);
  const canJoin = canJoinLiveSession(lv, now);
  const buttonLabel = getLiveJoinButtonLabel(lv, now);
  const showClassworkActions = isAttended(lv);
  const alreadyUploaded = hasClasswork(lv) || localUploaded;
  const previewUrl = hasClasswork(lv)
    ? liveClassworkPdfUrl(lv.classwork)
    : localPreviewUrl;

  const handleJoin = () => {
    if (!canJoin || !join) return;
    const attendanceId = getLiveAttendanceId(lv);
    if (attendanceId != null) {
      dispatch(setLiveVideoSeenRequest(attendanceId));
    }
    window.open(join, "_blank", "noopener,noreferrer");
  };

  const handlePickFile = () => {
    fileRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isPdf =
      file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error("Please upload a PDF file.");
      return;
    }
    const liveId = getLiveAttendanceId(lv);
    if (liveId == null) return;

    const formData = new FormData();
    formData.append("live_video_id", liveId);
    formData.append("zoom_id", liveId);
    formData.append("classwork", file);

    const objectUrl = URL.createObjectURL(file);
    dispatch(
      uploadLiveClassworkRequest(formData, () => {
        setLocalUploaded(true);
        setLocalPreviewUrl(objectUrl);
      })
    );
  };

  return (
    <>
      <VStack align={fullWidth ? "stretch" : "flex-end"} spacing={2} w={fullWidth ? "full" : "auto"}>
        <Button
          w={fullWidth ? "full" : "auto"}
          size={size}
          bg={C.secondary}
          color="white"
          fontWeight="800"
          textTransform="uppercase"
          fontSize="10px"
          borderRadius="lg"
          isDisabled={!canJoin}
          cursor={canJoin ? "pointer" : "not-allowed"}
          _disabled={{ bg: C.secondary, opacity: 0.55 }}
          _hover={canJoin ? { bg: C.primary } : {}}
          onClick={handleJoin}
          fontVariantNumeric="tabular-nums"
          title={phase === "ended" ? "Join window closed (2 hours after start)" : undefined}
        >
          {buttonLabel}
        </Button>

        {showClassworkActions ? (
          alreadyUploaded ? (
            <Button
              w={fullWidth ? "full" : "auto"}
              size={size}
              variant="outline"
              borderColor={C.primary}
              color={C.primary}
              fontWeight="800"
              textTransform="uppercase"
              fontSize="10px"
              borderRadius="lg"
              isDisabled={!previewUrl}
              onClick={() => setPreviewOpen(true)}
              _hover={{ bg: `${C.primary}12` }}
            >
              Preview classwork
            </Button>
          ) : (
            <>
              <Input
                ref={fileRef}
                type="file"
                accept=".pdf,application/pdf"
                display="none"
                onChange={handleFileChange}
              />
              <Button
                w={fullWidth ? "full" : "auto"}
                size={size}
                variant="outline"
                borderColor={C.primary}
                color={C.primary}
                fontWeight="800"
                textTransform="uppercase"
                fontSize="10px"
                borderRadius="lg"
                isLoading={seenLoading}
                onClick={handlePickFile}
                _hover={{ bg: `${C.primary}12` }}
              >
                Upload classwork PDF
              </Button>
            </>
          )
        ) : null}
      </VStack>

      <PdfPreviewModal
        isOpen={previewOpen && Boolean(previewUrl)}
        onClose={() => setPreviewOpen(false)}
        pdfUrl={previewUrl || ""}
        title={`${liveTitle(lv)} — classwork`}
        backLabel="Back"
      />
    </>
  );
}

/**
 * Homepage widget listing student live sessions with start timers / join window.
 */
const LiveSessionsPanel = ({ sessions }) => {
  const list = useMemo(() => {
    const raw = Array.isArray(sessions) ? sessions : [];
    return raw.filter(Boolean);
  }, [sessions]);

  const now = useCountdownNow(list.length > 0, 1000);

  const sorted = useMemo(() => {
    void now;
    return [...list].sort((a, b) => {
      const phaseRank = (lv) => {
        const p = getLiveSessionPhase(lv?.start_time, Date.now());
        if (p === "open") return 0;
        if (p === "upcoming") return 1;
        if (p === "unscheduled") return 2;
        return 3;
      };
      return phaseRank(a) - phaseRank(b);
    });
  }, [list, now]);

  return (
    <Box
      p={{ base: 5, md: 6 }}
      bg={C.paper}
      borderWidth="1px"
      borderColor={C.border}
      borderRadius="lg"
      boxShadow="sm"
      w="full"
      minW={0}
      display="flex"
      flexDirection="column"
      maxH={{ base: "360px", md: "420px" }}
      h="full"
    >
      <Flex align="center" gap={3} mb={4} flexShrink={0}>
        <Flex
          w="36px"
          h="36px"
          borderRadius="md"
          bg="rgba(91, 13, 22, 0.1)"
          align="center"
          justify="center"
          color={C.primary}
          flexShrink={0}
        >
          <MdVideocam size={20} />
        </Flex>
        <Box minW={0}>
          <Text
            fontSize="sm"
            fontWeight="800"
            letterSpacing="0.08em"
            color={C.primary}
            textTransform="uppercase"
            mb={0.5}
            sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
          >
            Live sessions
          </Text>
          <Text fontSize="xs" fontWeight="500" color={C.muted}>
            Join class streams
          </Text>
        </Box>
      </Flex>

      {sorted.length === 0 ? (
        <Text fontSize="sm" color={C.muted} lineHeight="tall">
          No live sessions right now. When a session is scheduled, it will show up here.
        </Text>
      ) : (
        <Box
          className="scroll"
          flex="1"
          minH={0}
          overflowY="auto"
          overflowX="hidden"
          pr={1}
          css={thinScrollCss}
        >
          <VStack align="stretch" spacing={2.5}>
            {sorted.map((lv) => {
              const phase = getLiveSessionPhase(lv?.start_time, now);
              return (
                <Flex
                  key={lv.id ?? `${liveTitle(lv)}-${lv.start_time}`}
                  direction="column"
                  align="stretch"
                  gap={2.5}
                  p={3}
                  bg={studentSurfaces.muted}
                  borderWidth="1px"
                  borderColor={C.border}
                  borderRadius="md"
                >
                  <Flex align="center" gap={3} minW={0}>
                    <Box
                      color={C.primary}
                      flexShrink={0}
                      sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
                    >
                      <MdVideocam size={18} />
                    </Box>
                    <Box flex="1" minW={0}>
                      <Text
                        fontSize="sm"
                        fontWeight="700"
                        color={C.secondary}
                        noOfLines={2}
                        sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
                      >
                        {liveTitle(lv)}
                      </Text>
                      <Text fontSize="xs" color={C.muted} fontWeight="600" mt={0.5} noOfLines={1}>
                        {phase === "ended"
                          ? "Session window closed"
                          : phase === "open"
                            ? "Live now"
                            : phase === "upcoming"
                              ? "Scheduled"
                              : "Live session"}
                      </Text>
                    </Box>
                  </Flex>
                  <LiveSessionJoinControls lv={lv} size="xs" fullWidth />
                </Flex>
              );
            })}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default LiveSessionsPanel;
